<template>
    <m-pageframe title="Bulk Message Recipient Imports">
        <v-container fluid style="padding: 15px">
                    <imports-search-card
                :card_title="computed_card_title"
                :card_subtitle="card_subtitle"
                endpoint_base="/api/v1/imports/bulk-message-recipients"
                :additional_params="search_filter_params"
            >
                <template #actions>
                    <v-btn
                        outlined
                        class="mr-2"
                        :to="{ name: 'search-bulk-messages', params: { organization_id: $route.params.organization_id } }"
                    >
                        <v-icon left>mdi-email-multiple</v-icon>
                        View All Bulk Messages
                    </v-btn>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <v-btn
                                    color="primary"
                                    :disabled="!hasWriteAccess()"
                                    @click="onClickStartImport"
                                >
                                    Import Recipients...
                                </v-btn>
                            </span>
                        </template>
                        <span v-if="!hasWriteAccess()">You do not have permission to create imports. Only organization administrators can create or modify imports.</span>
                        <span v-else>Import</span>
                    </v-tooltip>
                </template>

                <template #filters>
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                v-model="selected_filter_bulk_message_id"
                                :items="bulk_message_filter_options"
                                label="Filter by Bulk Message"
                                :loading="loading_filter_bulk_message_lists"
                                :disabled="loading_filter_bulk_message_lists"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-filter"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="selected_filter_import_data_source"
                                :items="import_data_source_options"
                                label="Filter by Source"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-database-import"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="selected_filter_import_execution_mode"
                                :items="import_execution_mode_options"
                                label="Filter by Type"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-swap-horizontal"
                            />
                        </v-col>
                    </v-row>
                </template>

                <template #default="{ results, loading, footer_props, computed_no_data_text }">
                    <imports-data-table
                        v-if="loading || results != null"
                        :headers="table_headers"
                        :items="results || []"
                        :items-per-page="50"
                        :loading="loading"
                        loading_text="Loading bulk recipient imports..."
                        :no_data_text="computed_no_data_text"
                        :footer_props="footer_props"
                        import_type="bulk-message-recipients"
                        upload_route_name="bulk-message-recipients-import-upload"
                    />
                </template>
            </imports-search-card>
        </v-container>

        <!-- Bulk Message Selection Dialog -->
        <v-dialog v-model="bulk_message_dialog" max-width="600px">
            <v-card>
                <v-card-title>
                    Select Bulk Message
                    <v-spacer></v-spacer>
                    <v-btn icon @click="bulk_message_dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <p class="mb-4">Choose which bulk message in the edit state that you want to import recipients into:</p>

                    <v-select
                        v-model="selected_bulk_message_id"
                        :items="bulk_message_options"
                        label="Bulk Message"
                        :loading="loading_bulk_message_lists"
                        :disabled="loading_bulk_message_lists"
                        item-text="text"
                        item-value="value"
                        outlined
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="bulk_message_dialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="!selected_bulk_message_id"
                        @click="onConfirmListSelection"
                    >
                        Continue to Import
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </m-pageframe>
</template>

<script>
import ImportsSearchCard from '@/components/common/imports-search-card.vue';
import ImportsDataTable from '@/components/imports/imports-data-table.vue';

/**
 * Request queue to serialize calls to the bulk/messages/search endpoint.
 * This prevents race conditions when loadBulkMessages() and loadFilterBulkMessages()
 * are both called on mount (or route change), as they hit the same API endpoint.
 */
const bulk_message_request_queue = {
    chain: Promise.resolve(),
    pending_count: 0
};

function enqueueBulkMessageRequest(request_fn)
{
    bulk_message_request_queue.pending_count++;
    const last_chain = bulk_message_request_queue.chain;

    const current_chain = last_chain.catch(() => null).then(() => request_fn());

    const handled_chain = current_chain.then(
        (result) => {
            bulk_message_request_queue.pending_count--;
            if (bulk_message_request_queue.pending_count === 0) {
                bulk_message_request_queue.chain = Promise.resolve();
            }
            return result;
        },
        (error) => {
            bulk_message_request_queue.pending_count--;
            if (bulk_message_request_queue.pending_count === 0) {
                bulk_message_request_queue.chain = Promise.resolve();
            }
            throw error;
        }
    );

    bulk_message_request_queue.chain = handled_chain;
    return handled_chain;
}

export default {
    components: { ImportsSearchCard, ImportsDataTable },
    name: 'SearchBulkMessageRecipientImports',


    data() {
        return {
            // For modal (edit state only)
            loading_bulk_message_lists: false,
            bulk_message_errors: null,
            bulk_message_lists: [],
            // For filter dropdown (all states)
            loading_filter_bulk_message_lists: false,
            filter_bulk_message_lists: [],
            bulk_message_dialog: false,
            selected_bulk_message_id: null,
            selected_filter_bulk_message_id: null,
            selected_filter_import_data_source: null,
            selected_filter_import_execution_mode: null,
            table_headers: [
                { text: 'Date', value: 'date', sortable: false },
                { text: 'Time', value: 'time', sortable: false },
                { text: 'Imported By', value: 'imported_by', sortable: false },
                { text: 'Original File', value: 'file_name', sortable: false },
                { text: 'Mapped File', value: 'enriched_data', sortable: false },
                { text: 'Status', value: 'state', sortable: false },
                { text: 'Source', value: 'import_data_source', sortable: false },
                { text: 'Target Bulk Message', value: 'target_bulk_message_names', sortable: false },
                { text: 'Recipients', value: 'recipients_added', sortable: false, align: 'center' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],
        };
    },

    computed: {
        footer_props() {
            return {
                itemsPerPageText: 'Imports per page:',
                itemsPerPageOptions: [25, 50, 100],
                disableItemsPerPage: false,
                disablePagination: false
            };
        },
        computed_no_data_text() {
            return 'No bulk recipient imports available';
        },

        // Filter params based on selected filter value
        search_filter_params() {
            const params = {};
            if (this.selected_filter_bulk_message_id) {
                params.bulk_message_id = this.selected_filter_bulk_message_id;
            }
            if (this.selected_filter_import_data_source) {
                params.import_data_source = this.selected_filter_import_data_source;
            }
            if (this.selected_filter_import_execution_mode) {
                params.import_execution_mode = this.selected_filter_import_execution_mode;
            }
            return params;
        },

        import_data_source_options() {
            const options = [];
            const enum_choices = this.$rc.enumChoices('import_data_source');
            
            if (enum_choices && enum_choices.length > 0) {
                for (let i = 0; i < enum_choices.length; i++) {
                    const choice = enum_choices[i];
                    if (choice.value && choice.value !== '') {
                        options.push({
                            value: choice.value,
                            text: choice.text
                        });
                    }
                }
            }
            
            return options;
        },

        import_execution_mode_options() {
            const options = [];
            const enum_choices = this.$rc.enumChoices('import_execution_mode');
            
            if (enum_choices && enum_choices.length > 0) {
                for (let i = 0; i < enum_choices.length; i++) {
                    const choice = enum_choices[i];
                    if (choice.value && choice.value !== '') {
                        options.push({
                            value: choice.value,
                            text: choice.text
                        });
                    }
                }
            }
            
            return options;
        },

        card_subtitle() {
            return 'Includes both recipient additions and removals from bulk messages';
        },

        // Computed title that shows when filtering
        computed_card_title() {
            if (this.selected_filter_bulk_message_id) {
                return 'Bulk Message Recipient Import History (Filtered)';
            }
            return 'Bulk Message Recipient Import History';
        },

        // Options for the filter dropdown (all bulk messages)
        bulk_message_filter_options() {
            const options = [];

            if (this.filter_bulk_message_lists && this.filter_bulk_message_lists.length > 0) {
                this.filter_bulk_message_lists.forEach(message => {
                    options.push({
                        value: message.bulk_message_id,
                        text: message.label
                    });
                });
            }

            return options;
        },

        bulk_message_options() {
            const options = [];

            // Show loading state
            if (this.loading_bulk_message_lists) {
                options.push({ value: '', text: 'Loading bulk messages...', disabled: true });
                return options;
            }

            // Show error state
            if (this.bulk_message_lists_error) {
                options.push({ value: '', text: 'Error loading bulk messages', disabled: true });
                return options;
            }

            // Add loaded bulk messages
            if (this.bulk_message_lists && this.bulk_message_lists.length > 0) {
                this.bulk_message_lists.forEach(message => {
                    options.push({
                        value: message.bulk_message_id,
                        text: message.label
                    });
                });
            } else {
                options.push({ value: '', text: 'No bulk messages available', disabled: true });
            }

            return options;
        }
    },

    created() {
        // Initialize filter from query param if present
        // This must be in created() so the value is set before child components mount
        if (this.$route.query.bulk_message_id) {
            this.selected_filter_bulk_message_id = this.$route.query.bulk_message_id;
        }
    },

    async mounted() {
        this.loadBulkMessages();
        this.loadFilterBulkMessages();
    },

    watch: {
        '$route'(to, from) {
             if (to.path !== from.path || to.params.organization_id !== from.params.organization_id) {
                 this.loadBulkMessages();
                 this.loadFilterBulkMessages();
             }
        }
    },

    methods: {
        hasWriteAccess() {
            // Check if current user has write access to contact list imports
            const organization_id = this.$route.params.organization_id;

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                // RedChirp staff can write in any organization (UI will warn on real accounts)
                return true;
            } else if (this.$rc.$state.isOrganizationAdmin(organization_id)) {
                // Organization admins can write to their organization
                return true;
            }

            // Regular users cannot write
            return false;
        },

        onConfirmListSelection() {
            if (!this.selected_bulk_message_id) {
                return;
            }

            this.bulk_message_dialog = false;

            this.$router.push({
                name: 'bulk-message-recipients-import-upload',
                params: {
                    organization_id: this.$route.params.organization_id
                },
                query: {
                    bulk_message_id: this.selected_bulk_message_id
                }
            });
        },
        onClickStartImport() {
            this.bulk_message_dialog = true;
        },
        
        // Load bulk messages in edit state (for modal selection)
        loadBulkMessages() {
            this.loading_bulk_message_lists = true;
            this.bulk_message_errors = null;

            // Enqueue the request to serialize with other bulk message searches
            enqueueBulkMessageRequest(() => {
                return new Promise((resolve, reject) => {
                    this.$rc.rest({
                        spinner: false,
                        endpoint: 'LIST: /api/v1/bulk/messages/search',
                        params: {
                            organization_id: this.$route.params.organization_id,
                            state: "edit",
                            is_archived: false,
                            rc_tag_ids: null
                        },
                        on_success: (data) => {
                            resolve(data);
                        },
                        on_error: (error) => {
                            reject(error);
                        }
                    });
                });
            })
                .then((data) => {
                    this.bulk_message_lists = (data && Array.isArray(data.results)) ? data.results : [];
                    this.bulk_message_errors = null;
                    this.loading_bulk_message_lists = false;
                })
                .catch((error) => {
                    this.loading_bulk_message_lists = false;
                    this.bulk_message_errors = 'Failed to load bulk messages';
                    this.bulk_message_lists = [];

                    if (error && error.message)
                    {
                        this.$rc.snackbar(`Failed to load bulk messages: ${error.message}`, 'error');
                    }
                    else
                    {
                        this.$rc.snackbar('Failed to load bulk messages. Please refresh the page to try again.', 'error');
                    }
                });
        },

        // Load all bulk messages (for filter dropdown)
        loadFilterBulkMessages() {
            this.loading_filter_bulk_message_lists = true;

            // Enqueue the request to serialize with other bulk message searches
            enqueueBulkMessageRequest(() => {
                return new Promise((resolve, reject) => {
                    this.$rc.rest({
                        spinner: false,
                        endpoint: 'LIST: /api/v1/bulk/messages/search',
                        params: {
                            organization_id: this.$route.params.organization_id,
                            is_archived: false,
                            rc_tag_ids: null
                        },
                        on_success: (data) => {
                            resolve(data);
                        },
                        on_error: (error) => {
                            reject(error);
                        }
                    });
                });
            })
                .then((data) => {
                    this.filter_bulk_message_lists = (data && Array.isArray(data.results)) ? data.results : [];
                    this.loading_filter_bulk_message_lists = false;
                })
                .catch(() => {
                    this.loading_filter_bulk_message_lists = false;
                    this.filter_bulk_message_lists = [];
                });
        },

        onBulkMessageSelected(bulkMessage) {
            this.bulk_message_dialog = false;
            if (!bulkMessage || !bulkMessage.bulk_message_id) {
                return;
            }
            const organization_id = this.$route.params.organization_id;
            const bulk_message_id = bulkMessage.bulk_message_id;
            this.$router.push({
                path: `/app/bulk/messages/${organization_id}/imports/upload`,
                query: { bulk_message_id }
            });
        }
    }
};
</script>
